import { BlockOutlined, CheckCircleOutlined, EditOutlined } from '@mui/icons-material';
import {
  CardContainer,
  DataTable,
  Tag,
  type DataTableColumn,
  type DataTableRow,
} from 'finara-components';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { CsvExportButton, ConfirmationModal } from '@/components/common';
import { ErrorState } from '@/components/common/ErrorState';
import { LoadingState } from '@/components/common/LoadingState';
import { EditOrganizationModal } from '@/components/super-admin/clients/EditOrganizationModal';
import { useAccounts } from '@/hooks/queries/useAccounts';
import { useUpdateOrganization } from '@/hooks/queries/useOrganizations';
import { useClientList } from '@/hooks/queries/useSuperAdminClients';
import type { UpdateOrganizationData } from '@/types/credits';
import type { ClientListQuery, OrganizationClient } from '@/types/superAdminClients';

interface ClientTableProps {
  searchQuery: string;
  statusFilter: string;
  typeFilter: string;
}

const StatusTag: React.FC<{ status: 'Activo' | 'Suspendido' }> = ({ status }) => {
  const statusConfig = {
    Activo: { label: 'Activo', variant: 'green' as const },
    Suspendido: { label: 'Suspendido', variant: 'red' as const },
  };

  const config = statusConfig[status];
  return <Tag variant={config.variant}>{config.label}</Tag>;
};

export const ClientTable: React.FC<ClientTableProps> = ({
  searchQuery,
  statusFilter,
  typeFilter,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [editClient, setEditClient] = useState<OrganizationClient | null>(null);
  const [statusClient, setStatusClient] = useState<OrganizationClient | null>(null);
  const limit = 10;

  const updateOrganization = useUpdateOrganization();
  const { data: accountsData } = useAccounts({
    type: 'organization',
    status: 'active',
    limit: 1000,
  });

  const clabeByOrgId = useMemo(() => {
    const map = new Map<string, string>();
    accountsData?.data.forEach(a => {
      if (a.organization_id) map.set(a.organization_id, a.clabe);
    });
    return map;
  }, [accountsData]);

  // Build query based on filters
  const query: ClientListQuery = useMemo(() => {
    const q: ClientListQuery = {
      page: currentPage,
      limit,
      orderDirection: 'DESC',
    };

    if (searchQuery) q.search = searchQuery;
    if (statusFilter !== 'all') {
      q.status = statusFilter as 'Activo' | 'Suspendido';
    }
    if (typeFilter !== 'all') {
      q.clientType = typeFilter as 'Empresa' | 'Persona Física';
    }

    return q;
  }, [currentPage, searchQuery, statusFilter, typeFilter]);

  const { data, isLoading, isError, refetch } = useClientList(query);

  // Calculate total pages
  const totalPages = data ? Math.ceil(data.count / limit) : 0;

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const columns: DataTableColumn[] = [
    {
      key: 'name',
      label: 'Nombre / Razón Social',
      width: '25%',
    },
    {
      key: 'code',
      label: 'Código',
      width: '15%',
    },
    {
      key: 'clabe',
      label: 'CLABE',
      width: '15%',
    },
    {
      key: 'type',
      label: 'Tipo de Cliente',
      width: '12%',
    },
    {
      key: 'status',
      label: 'Estatus',
      width: '10%',
    },
    {
      key: 'registrationDate',
      label: 'Fecha de Alta',
      width: '12%',
    },
    {
      key: 'lastActivity',
      label: 'Última Actividad',
      width: '12%',
    },
    {
      key: 'volume',
      label: 'Volumen 30d',
      width: '10%',
    },
    {
      key: 'saldoPrincipal',
      label: 'Saldo Principal',
      width: '10%',
    },
    {
      key: 'saldoGeneral',
      label: 'Saldo General',
      width: '10%',
    },
    {
      key: 'saldoClientes',
      label: 'Saldo Clientes',
      width: '10%',
    },
    {
      key: 'actions',
      label: 'Acciones',
      width: '8%',
    },
  ];

  const handleEditSubmit = (data: UpdateOrganizationData) => {
    if (!editClient) return;
    if (Object.keys(data).length === 0) {
      setEditClient(null);
      return;
    }
    updateOrganization.mutate(
      { id: editClient.id, data },
      {
        onSuccess: () => {
          toast.success('Organización actualizada');
          setEditClient(null);
        },
        onError: error => {
          toast.error(
            `Error al actualizar: ${error instanceof Error ? error.message : 'Error desconocido'}`
          );
        },
      }
    );
  };

  const handleStatusToggle = () => {
    if (!statusClient) return;
    const willDeactivate = statusClient.status === 'Activo';
    updateOrganization.mutate(
      { id: statusClient.id, data: { status: willDeactivate ? 'inactive' : 'active' } },
      {
        onSuccess: () => {
          toast.success(willDeactivate ? 'Organización desactivada' : 'Organización activada');
          setStatusClient(null);
        },
        onError: error => {
          toast.error(
            `Error al cambiar el estado: ${error instanceof Error ? error.message : 'Error desconocido'}`
          );
        },
      }
    );
  };

  const dataRows: DataTableRow[] =
    data?.data.map(client => ({
      id: client.id,
      name: (
        <div>
          <div className="font-medium text-brand-white">{client.name}</div>
          <div className="text-sm text-muted">Empleados: {client.employeeCount || 0}</div>
        </div>
      ),
      code: client.code || 'N/A',
      clabe: (
        <span className="font-mono text-sm text-brand-white">
          {clabeByOrgId.get(client.id) || 'N/A'}
        </span>
      ),
      type: client.clientType,
      status: <StatusTag status={client.status} />,
      registrationDate: formatDate(client.registrationDate),
      lastActivity: formatDate(client.lastActivity),
      volume: `$${client.totalVolume30d.toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      saldoPrincipal: `$${(client.saldoPrincipal ?? 0).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      saldoGeneral: `$${(client.saldoGeneral ?? 0).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      saldoClientes: `$${(client.saldoClientes ?? 0).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      actions: (
        <div className="flex items-center gap-1">
          <button
            type="button"
            title="Editar organización"
            className="p-1.5 text-muted hover:text-brand-white rounded hover:bg-black/5 dark:hover:bg-white/5"
            onClick={e => {
              e.stopPropagation();
              setEditClient(client);
            }}
          >
            <EditOutlined fontSize="small" />
          </button>
          <button
            type="button"
            title={client.status === 'Activo' ? 'Desactivar' : 'Activar'}
            className="p-1.5 text-muted hover:text-brand-white rounded hover:bg-black/5 dark:hover:bg-white/5"
            onClick={e => {
              e.stopPropagation();
              setStatusClient(client);
            }}
          >
            {client.status === 'Activo' ? (
              <BlockOutlined fontSize="small" />
            ) : (
              <CheckCircleOutlined fontSize="small" />
            )}
          </button>
        </div>
      ),
    })) ?? [];

  const handleRowClick = (row: DataTableRow) => {
    void navigate(`/clientes/${row.id}`);
  };

  if (isError) {
    return (
      <ErrorState
        title="Error al cargar los clientes"
        message="No se pudieron cargar los clientes. Por favor, intenta nuevamente."
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <CardContainer>
      <div className="px-4 pb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-brand-white">
          Clientes {!isLoading && data ? `(${data.count})` : ''}
        </h3>
        <CsvExportButton
          data={data?.data ?? []}
          filename="clientes"
          columns={client => ({
            Nombre: client.name,
            Código: client.code || 'N/A',
            CLABE: clabeByOrgId.get(client.id) || 'N/A',
            'Tipo de Cliente': client.clientType,
            Estatus: client.status,
            'Fecha de Alta': formatDate(client.registrationDate),
            'Última Actividad': formatDate(client.lastActivity),
            'Volumen 30d': `$${client.totalVolume30d.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            Empleados: client.employeeCount || 0,
            'Saldo Principal': `$${(client.saldoPrincipal ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            'Saldo General': `$${(client.saldoGeneral ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            'Saldo Clientes': `$${(client.saldoClientes ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          })}
          variant="white"
          size="small"
          buttonText="Exportar"
        />
      </div>

      {isLoading ? (
        <LoadingState message="Cargando clientes..." fullHeight={false} />
      ) : (
        <>
          <div className="w-full overflow-scroll">
            <DataTable
              columns={columns}
              data={dataRows}
              onRowClick={handleRowClick}
              rowClassName={(_, index) => (index % 2 === 0 ? 'dark:bg-brand-blue/10' : '')}
            />
          </div>

          {/* Pagination */}
          {data && totalPages > 1 && (
            <div className="flex justify-between items-center px-4 py-4 border-t border-border">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-border rounded text-brand-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/5"
              >
                Anterior
              </button>
              <span className="text-sm text-muted">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-border rounded text-brand-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/5"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}

      {/* Edit Organization Modal */}
      <EditOrganizationModal
        isOpen={!!editClient}
        onClose={() => setEditClient(null)}
        onSubmit={handleEditSubmit}
        organizationName={editClient?.name ?? ''}
        isLoading={updateOrganization.isPending}
      />

      {/* Activate / Deactivate confirmation */}
      <ConfirmationModal
        isOpen={!!statusClient}
        onClose={() => setStatusClient(null)}
        onConfirm={handleStatusToggle}
        title={
          statusClient?.status === 'Activo'
            ? 'Desactivar organización'
            : 'Activar organización'
        }
        message={
          statusClient?.status === 'Activo'
            ? `¿Seguro que deseas desactivar a "${statusClient?.name}"? La organización quedará suspendida.`
            : `¿Seguro que deseas activar a "${statusClient?.name ?? ''}"?`
        }
        confirmText={statusClient?.status === 'Activo' ? 'Desactivar' : 'Activar'}
        variant={statusClient?.status === 'Activo' ? 'danger' : 'info'}
        isLoading={updateOrganization.isPending}
      />
    </CardContainer>
  );
};
